import * as React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import moment from 'moment';

import { AH$RouterLocation } from 'types';
import thematize from 'lib/thematize';
import { NavigateFunction } from 'hooks/useNavigate';
import messages from 'components/AdminPanel/Companies/messages';
import Icon from 'components/Base/Icon';
import BaseTable from 'components/Base/Table';
import Sort, { Order } from 'components/Base/Table/Sort';
import styles from './Table.scss';

const theme = thematize(styles);

interface Props {
  companies: Array<AH$CompanyShort>;
  location: AH$RouterLocation;
  navigate: NavigateFunction;
}

const HEADERS = [
  'id',
  'name',
  'site',
  'paid_till',
  'max_number_employees',
  'account_manager__first_name',
];

const Table = ({ navigate, location, companies }: Props): React.ReactElement => {
  const { formatMessage } = useIntl();
  const { ordering = 'id' } = location.query;

  const getOrder = (id: string): Order => {
    if (ordering.includes(id)) {
      if (ordering.startsWith('-')) return Order.Down;
      return Order.Up;
    }
    return Order.Default;
  };

  const handleSort = (id: string): void => {
    navigate({
      query: {
        ...location.query,
        ordering: id === ordering ? `-${id}` : id,
      },
    });
  };

  return (
    <BaseTable className={theme('container')}>
      <thead>
        <tr>
          {HEADERS.map((id) => (
            <th key={id}>
              <div className={theme('header')}>
                <Sort order={getOrder(id)} onClick={(): void => handleSort(id)} />
                <span className={theme('title')} onClick={(): void => handleSort(id)}>
                  <FormattedMessage {...messages[id as keyof typeof messages]} />
                </span>
              </div>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {companies.map(
          ({ id, name, site, active, paidTill, maxNumberEmployees, accountManager }) => (
            <tr key={id}>
              <td>{id}</td>
              <td>
                <Link to={`${id}/`}>
                  {name || (
                    <span className={theme('no-name')}>{formatMessage(messages.noName)}</span>
                  )}
                </Link>
                {!active && ` (${formatMessage(messages.inactive)})`}
              </td>
              <td>
                {site && (
                  <a href={site} target="_blank" rel="noopener noreferrer" aria-label="link">
                    <Icon type="externalLink" />
                  </a>
                )}
              </td>
              <td>{paidTill ? moment(paidTill).format('lll') : '∞'}</td>
              <td>{maxNumberEmployees || '∞'}</td>
              <td>{accountManager}</td>
            </tr>
          )
        )}
      </tbody>
    </BaseTable>
  );
};

export default Table;
